import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, OfflineError } from '../api/http';
import {
  configureServer,
  deleteServer,
  fetchServers,
  formatUptime,
  serverAction,
  type ServerRequest,
  type ServersResponse,
  type ServerView,
  downloadingIds,
  fetchServerLog,
  scanServerContent,
  switchServerMission,
  type LogTail,
} from '../api/servers';
import { fetchSummary, type CatalogueSummary } from '../api/catalog';
import { fetchMissions, type MissionCard } from '../api/missions';
import { fetchReports, type ReportView } from '../api/reports';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { ModMosaic, ModTile } from './ModMosaic';
import { ServerStateTag } from './ServerStateTag';
import { ServerForm } from './ServerForm';
import { missionOn, originOf, serverWorld } from './serverMods';
import { ServerRespawnCard } from './ServerRespawnCard';
import { ServerSettingCard } from './ServerSettingCard';
import { PageLoading } from './PageStates';
import { Shell } from './Shell';
import { TerrainImage } from './TerrainImage';
import { fmtDay } from './dates';

function errorText(err: unknown): string {
  if (err instanceof OfflineError) return ca.errors.offline;
  if (!(err instanceof ApiError)) return ca.errors.unknown;
  const base = ca.errors[err.code] ?? ca.errors.unknown;
  // A panel_declined carries the guard's own reason ("Server 'X' belongs to
  // the operator and is not linkable"), the one thing an admin needs to act
  // on a start, stop, restart, configure, delete or mission switch refused
  // here — thrown away until the reason followed the sentence, as it already
  // did on the session page (useSessionLive.errorText).
  return err.detail ? `${base} ${err.detail}` : base;
}

const MOD_SEARCH_THRESHOLD = 12;
/** How many of the server's last matches its page lists. */
const RECENT_MATCHES = 5;

/** Simplex Support Services, by the name the panel's library gives it (`SupportSystems.isSimplex`). */
const SIMPLEX_ID = 3029520419;
function isSimplex(mod: { id: number | null; name: string }): boolean {
  return mod.id === SIMPLEX_ID || mod.name.toLowerCase().startsWith('simplex support services');
}

/**
 * One server's page (`/servers/:id`): the list's data for this id (no
 * extra endpoint), under a header with its state as a tag, whose it is, its
 * port and its mods as a regular grid. The buttons follow the state: a running
 * server offers stopping and restarting, a stopped one starting.
 *
 * Then what a person asks of a server, in that order: what it is doing now
 * (the mission by its title, on its terrain), what was last played on it,
 * what it carries (the counts first, the whole list of mods folded), the eras
 * a night on it can be set in, and its configuration. Deletion sits in a
 * folded danger zone behind the typed-name confirmation.
 *
 * Two of those cards read something else than the servers' list, and each is
 * simply not drawn when its source does not answer: the last matches are the
 * reports the platform already mirrors, kept to this server's; the terrains
 * and factions are the catalogue's summary of the server's mod set, which
 * exists only once it has been scanned.
 */
export function ServerDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const [data, setData] = useState<ServersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const [modQuery, setModQuery] = useState('');
  const isAdmin = me.roles.includes('ADMIN');
  // The tail of the panel's log, asked for when the operator opens it and every
  // fifteen seconds while it stays open; the panel keeps a server's log after the
  // server is gone, so a dead server still says why.
  const [logOpen, setLogOpen] = useState(false);
  // Switching the mission: the platform's published missions, by folder; the handover
  // refuses a server with players on it, and the button says so before asking.
  const [published, setPublished] = useState<MissionCard[]>([]);
  const [switchFolder, setSwitchFolder] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    fetchMissions()
      .then((ms) => setPublished(ms.filter((m) => m.publishedFolder)))
      .catch(() => setPublished([]));
  }, []);
  // The reports the platform mirrors from the panel, for the last matches here;
  // null while unknown and when they cannot be read, and the card is not drawn.
  const [reports, setReports] = useState<ReportView[] | null>(null);
  useEffect(() => {
    fetchReports()
      .then((list) => setReports(list.reports))
      .catch(() => setReports(null));
  }, []);
  const [summary, setSummary] = useState<CatalogueSummary | null>(null);
  const [log, setLog] = useState<LogTail | null>(null);
  const loadLog = useCallback(async () => {
    if (!id) return;
    try {
      setLog(await fetchServerLog(Number(id)));
    } catch {
      setLog({ serverId: Number(id), lines: 0, tail: [] });
    }
  }, [id]);
  useEffect(() => {
    if (!logOpen) return;
    void loadLog();
    const t = setInterval(() => void loadLog(), 15000);
    return () => clearInterval(t);
  }, [logOpen, loadLog]);
  const t = ca.serverDetail;
  const s = ca.serversPage;

  const load = useCallback(async () => {
    try {
      setData(await fetchServers());
      setError(null);
    } catch (err) {
      setData(null);
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (me.authenticated) void load();
  }, [me.authenticated, load]);

  const server = useMemo(
    () => data?.servers.find((sv) => String(sv.id) === id) ?? null,
    [data, id],
  );

  const mods = useMemo(() => {
    const list = server?.activeMods ?? [];
    const q = modQuery.trim().toLowerCase();
    return q ? list.filter((m) => m.name.toLowerCase().includes(q)) : list;
  }, [server, modQuery]);

  const recent = useMemo(
    () =>
      reports === null
        ? null
        : reports
            .filter((r) => String(r.panelServerId) === id)
            .sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''))
            .slice(0, RECENT_MATCHES),
    [reports, id],
  );

  // What the catalogue knows of the server's mod set, once it has been scanned.
  const scannedHash = server?.scanned ? (server.modSetHash ?? null) : null;
  useEffect(() => {
    setSummary(null);
    if (!scannedHash) return undefined;
    let current = true;
    fetchSummary(scannedHash)
      .then((found) => {
        if (current) setSummary(found);
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [scannedHash]);

  async function act(action: 'start' | 'stop' | 'restart' | 'link' | 'unlink') {
    if (!server) return;
    setBusy(true);
    try {
      await serverAction(server.id, action);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function switchMission() {
    if (!server || !switchFolder) return;
    setBusy(true);
    setNotice(null);
    try {
      await switchServerMission(server.id, switchFolder);
      setNotice(s.switchDone(switchFolder));
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function scanContent() {
    if (!server) return;
    setBusy(true);
    setNotice(null);
    try {
      const scan = await scanServerContent(server.id);
      setNotice(s.scanStarted(scan.status));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitForm(input: ServerRequest) {
    if (!server) return;
    setFormError(null);
    setDownloading([]);
    try {
      await configureServer(server.id, input);
      setConfiguring(false);
      await load();
    } catch (err) {
      setFormError(errorText(err));
      setDownloading(downloadingIds(err));
    }
  }

  async function confirmDelete() {
    if (!server || deleteName !== server.name) return;
    setBusy(true);
    try {
      await deleteServer(server.id);
      navigate('/servers');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!data && !error) {
    return (
      <Shell title={ca.servers}>
        <PageLoading />
      </Shell>
    );
  }

  if (!server) {
    return (
      <Shell title={ca.servers}>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {!error && (
          <div className="empty">
            <i className="bi bi-hdd-network" aria-hidden="true" />
            <p>{t.notFound}</p>
            <Link className="button small" to="/servers">
              {t.backToList}
            </Link>
          </div>
        )}
      </Shell>
    );
  }

  const up = formatUptime(server.status.startedAt);
  const can = (a: ServerView['actions'][number]) => server.actions.includes(a);
  const canManage = isAdmin || me.roles.includes('SERVER_MANAGER');
  const budgetLeft = data ? data.budget.max - data.budget.used : 0;
  const alive = server.status.alive;
  const origin = originOf(server.access);
  const world = serverWorld(server);
  // What is on now; the configured mission, which the panel keeps whether or not
  // the server runs, is in the configuration card.
  const mission = missionOn(server);
  const configuredMission = server.missionTitle ?? server.missionTemplate;
  const dlcs = server.dlcs ?? [];
  const carries = [
    s.modsCount(server.activeMods.length),
    ...(dlcs.length > 0 ? [t.dlcCount(dlcs.length)] : []),
    ...(server.activeMods.some(isSimplex) ? [t.simplex] : []),
  ];

  return (
    <Shell
      title={server.name}
      kicker={
        <Link to="/servers">
          <i className="bi bi-chevron-left" aria-hidden="true" /> {t.kicker(server.port)}
        </Link>
      }
      actions={
        <button type="button" className="ghost" onClick={() => void load()}>
          <i className="bi bi-arrow-clockwise" aria-hidden="true" />
          {ca.refresh}
        </button>
      }
    >
      <div className="server-detail">
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <section className="card server-hero" aria-label={server.name}>
          <div className="row chips">
            <ServerStateTag alive={alive} uptime={up} />
            <span className="chip" title={ca.accessHint[server.access]}>
              {origin.label}
              {origin.note && <> · {origin.note}</>}
            </span>
            <span className="chip">{s.portChip(server.port)}</span>
            <span className="chip">
              <i className="bi bi-box-seam" aria-hidden="true" />{' '}
              {s.modsCount(server.activeMods.length)}
            </span>
            {server.opsMod && (
              <span className="chip ok" title={s.opsModHint}>
                <i className="bi bi-puzzle" aria-hidden="true" /> {s.opsMod}
              </span>
            )}
          </div>
          <ModMosaic mods={server.activeMods} wide />
        </section>
        {server.access === 'NONE' && <p className="notice warn">{ca.accessHint.NONE}</p>}

        <div className="row actions">
          {can('start') && !alive && (
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void act('start')}
            >
              <i className="bi bi-play-fill" aria-hidden="true" />
              {ca.start}
            </button>
          )}
          {can('stop') && alive && (
            <button type="button" disabled={busy} onClick={() => void act('stop')}>
              <i className="bi bi-stop-fill" aria-hidden="true" />
              {ca.stop}
            </button>
          )}
          {can('restart') && alive && (
            <button type="button" disabled={busy} onClick={() => void act('restart')}>
              <i className="bi bi-arrow-repeat" aria-hidden="true" />
              {ca.restart}
            </button>
          )}
          {can('configure') && (
            <button
              type="button"
              disabled={busy}
              aria-pressed={configuring}
              onClick={() => {
                setFormError(null);
                setConfiguring((c) => !c);
              }}
            >
              <i className="bi bi-sliders" aria-hidden="true" />
              {ca.configure}
            </button>
          )}
          {canManage && (
            <button type="button" disabled={busy} onClick={() => void scanContent()}>
              <i className="bi bi-search" aria-hidden="true" />
              {s.scanContent}
            </button>
          )}
          {canManage && (
            <button type="button" onClick={() => setLogOpen((o) => !o)} aria-expanded={logOpen}>
              <i className="bi bi-terminal" aria-hidden="true" />
              {logOpen ? s.logHide : s.logTail}
            </button>
          )}
          {isAdmin && can('link') && (
            <button type="button" disabled={busy} onClick={() => void act('link')}>
              <i className="bi bi-link-45deg" aria-hidden="true" />
              {ca.link}
            </button>
          )}
          {isAdmin && can('unlink') && (
            <button type="button" disabled={busy} onClick={() => void act('unlink')}>
              <i className="bi bi-x-lg" aria-hidden="true" />
              {ca.unlink}
            </button>
          )}
        </div>
        {canManage && can('mission') && (
          <div className="row actions">
            <label className="inline">
              {s.switchMission}
              <select
                value={switchFolder}
                onChange={(e) => setSwitchFolder(e.target.value)}
                aria-label={s.switchMission}
              >
                <option value="">{s.switchPick}</option>
                {published.map((m) => (
                  <option key={m.code} value={m.publishedFolder ?? ''}>
                    {m.title} · {m.worldDisplayName ?? m.worldName}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={
                busy ||
                !switchFolder ||
                Math.max(0, server.status.playersOnline - server.status.headlessClientsCount) > 0
              }
              title={
                Math.max(0, server.status.playersOnline - server.status.headlessClientsCount) > 0
                  ? s.switchHasPlayers
                  : undefined
              }
              onClick={() => void switchMission()}
            >
              <i className="bi bi-arrow-left-right" aria-hidden="true" />
              {s.switchMission}
            </button>
          </div>
        )}
        {notice && (
          <p className="muted small" role="status">
            {notice}
          </p>
        )}
        {logOpen && (
          <section className="card" aria-label={s.logTail}>
            <p className="row muted small">
              <span>{log ? s.logLines(log.lines) : ca.loading}</span>
              <button type="button" className="small" onClick={() => void loadLog()}>
                {s.logRefresh}
              </button>
            </p>
            {log && log.tail.length === 0 && <p className="muted small">{s.logEmpty}</p>}
            {log && log.tail.length > 0 && (
              <pre className="log-tail" tabIndex={0}>
                {log.tail.join('\n')}
              </pre>
            )}
          </section>
        )}

        {configuring && (
          <ServerForm
            key={server.id}
            server={server}
            budgetLeft={budgetLeft}
            onSubmit={submitForm}
            onCancel={() => setConfiguring(false)}
            error={formError}
            downloading={downloading}
            inline
          />
        )}

        <div className="dashboard">
          <article className="card now-card" aria-label={t.nowTitle}>
            <h2>{t.nowTitle}</h2>
            <div className="now">
              {world && <TerrainImage world={world} label={world} className="thumb now-thumb" />}
              <div className="now-text">
                {mission ? (
                  <strong title={server.missionTemplate ?? undefined}>{mission}</strong>
                ) : (
                  <span className="muted">{alive ? t.noMission : t.nowStopped}</span>
                )}
                {world && <span className="muted">{world}</span>}
              </div>
            </div>
            <dl className="facts">
              <div>
                <dt>{ca.players}</dt>
                <dd>{s.playersChip(server.status.playersOnline, server.maxPlayers)}</dd>
              </div>
              <div>
                <dt>{t.headless}</dt>
                <dd>{server.status.headlessClientsCount}</dd>
              </div>
              <div>
                <dt>{s.uptimeShort}</dt>
                <dd>{up ?? '—'}</dd>
              </div>
            </dl>
          </article>

          {recent && (
            <article className="card" aria-label={t.recentTitle}>
              <h2>{t.recentTitle}</h2>
              {recent.length === 0 && <p className="muted small">{t.recentNone}</p>}
              {recent.length > 0 && (
                <ul className="recent-matches plain">
                  {recent.map((r) => {
                    const title = r.displayTitle ?? r.missionName ?? r.world ?? '—';
                    return (
                      <li key={r.id}>
                        {r.reportUrl ? (
                          <a href={r.reportUrl} target="_blank" rel="noreferrer">
                            {title}
                          </a>
                        ) : (
                          <span>{title}</span>
                        )}
                        <span className="muted small">
                          {[
                            r.startedAt ? fmtDay(r.startedAt) : null,
                            r.durationSeconds > 0 ? ca.reportDuration(r.durationSeconds) : null,
                            t.recentPlayers(r.playerCount),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Link className="small" to="/reports">
                {t.recentAll} <i className="bi bi-chevron-right" aria-hidden="true" />
              </Link>
            </article>
          )}

          <article className="card wide" aria-label={t.carriesTitle}>
            <h2>{t.carriesTitle}</h2>
            <p className="carries">{carries.join(' · ')}</p>
            <div className="row chips">
              {summary && (
                <>
                  <span className="chip">
                    <i className="bi bi-map" aria-hidden="true" /> {t.terrainsCount(summary.worlds)}
                  </span>
                  <span className="chip">
                    <i className="bi bi-flag" aria-hidden="true" />{' '}
                    {t.factionsCount(summary.factions)}
                  </span>
                </>
              )}
              {server.setting && server.setting.eras.length > 0 && (
                <span className="chip">
                  <i className="bi bi-hourglass-split" aria-hidden="true" />{' '}
                  {t.erasCount(server.setting.eras.length)}
                </span>
              )}
              {dlcs.map((dlc) => (
                <span className="chip" key={dlc}>
                  {dlc}
                </span>
              ))}
            </div>
            {server.scanned === false && server.activeMods.length > 0 && (
              <p className="muted small">{t.notScanned}</p>
            )}
            {server.activeMods.length === 0 && <p className="muted small">{t.modsNone}</p>}
            {server.activeMods.length > 0 && (
              <p>
                <a className="button" href={`/api/servers/${server.id}/mods.html`} download>
                  <i className="bi bi-download" aria-hidden="true" /> {ca.sessionMods.download}
                </a>{' '}
                <span className="muted small">{ca.sessionMods.hint}</span>
              </p>
            )}
            {server.activeMods.length > 0 && (
              <details className="fold mods-fold">
                <summary>{t.modsAll(server.activeMods.length)}</summary>
                <div className="fold-body">
                  {server.activeMods.length > MOD_SEARCH_THRESHOLD && (
                    <input
                      className="control"
                      type="search"
                      aria-label={t.modsSearch}
                      placeholder={t.modsSearch}
                      value={modQuery}
                      onChange={(e) => setModQuery(e.target.value)}
                    />
                  )}
                  {mods.length === 0 && <p className="muted small">{t.modsNoMatch}</p>}
                  {mods.length > 0 && (
                    <ul className="mod-list plain" aria-label={t.modsTitle}>
                      {mods.map((m, index) => (
                        <li key={m.id ?? `${m.name}-${index}`}>
                          <ModTile mod={m} className="mod-tile mini" />
                          <span>{m.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </details>
            )}
          </article>

          {server.setting && (
            <ServerSettingCard
              key={`${server.id}-${server.setting.confirmed}`}
              serverId={server.id}
              setting={server.setting}
              canManage={canManage}
              onSaved={() => void load()}
            />
          )}

          {server.lifecycle && (
            <ServerRespawnCard
              key={`${server.id}-${server.lifecycle.respawn?.mode ?? 'none'}`}
              serverId={server.id}
              current={server.lifecycle.respawn}
              canManage={canManage}
              onSaved={() => void load()}
            />
          )}

          <article className="card" aria-label={t.configTitle}>
            <h2>{t.configTitle}</h2>
            <dl className="facts">
              <div>
                <dt>{ca.maxPlayers}</dt>
                <dd>{server.maxPlayers}</dd>
              </div>
              <div>
                <dt>{ca.battlEye}</dt>
                <dd>{server.battlEye ? t.yes : t.no}</dd>
              </div>
              <div>
                <dt>{t.configuredMission}</dt>
                <dd title={server.missionTemplate ?? undefined}>{configuredMission ?? '—'}</dd>
              </div>
              <div>
                <dt>{ca.port}</dt>
                <dd>{server.port}</dd>
              </div>
              {server.flightModel && (
                <div title={s.flightModelHint}>
                  <dt>{t.flightModel}</dt>
                  <dd>{s.flightModel[server.flightModel]}</dd>
                </div>
              )}
              {server.lifecycle && (
                <>
                  <div>
                    <dt>{ca.headlessClients}</dt>
                    <dd>{server.lifecycle.headlessClients}</dd>
                  </div>
                  <div>
                    <dt>{ca.startBefore}</dt>
                    <dd>{server.lifecycle.startBeforeMinutes}</dd>
                  </div>
                  <div>
                    <dt>{ca.stopIdle}</dt>
                    <dd>{server.lifecycle.stopIdleMinutes}</dd>
                  </div>
                  <div>
                    <dt>{ca.keepForCampaign}</dt>
                    <dd>{server.lifecycle.keepForCampaign ? t.yes : t.no}</dd>
                  </div>
                  {!server.lifecycle.keepForCampaign && (
                    <div>
                      <dt>{ca.retentionDays}</dt>
                      <dd>{server.lifecycle.retentionDays}</dd>
                    </div>
                  )}
                </>
              )}
            </dl>
            {!server.lifecycle && <p className="muted small">{t.noLifecycle}</p>}
          </article>

          <article className="card" aria-label={t.panelStateTitle}>
            <h2>{t.panelStateTitle}</h2>
            <dl className="facts">
              <div>
                <dt>{t.version}</dt>
                <dd>{server.status.version ?? '—'}</dd>
              </div>
              <div>
                <dt>{t.map}</dt>
                <dd>{server.status.map ?? '—'}</dd>
              </div>
              <div>
                <dt>{t.description}</dt>
                <dd>{server.status.description ?? server.description ?? '—'}</dd>
              </div>
              <div>
                <dt>{t.type}</dt>
                <dd>{server.type}</dd>
              </div>
            </dl>
          </article>
        </div>

        {can('delete') && (
          <details className="danger-fold">
            <summary>
              <i className="bi bi-exclamation-triangle" aria-hidden="true" /> {t.dangerZone}
            </summary>
            <div className="card danger-zone">
              <p className="muted small">{t.dangerZoneHint}</p>
              <label className="form">
                {ca.deleteConfirm(server.name)}
                <input
                  className="control"
                  name="deleteName"
                  value={deleteName}
                  onChange={(e) => setDeleteName(e.target.value)}
                />
              </label>
              <div className="row actions">
                <span className="grow" />
                <button
                  type="button"
                  className="danger"
                  disabled={deleteName !== server.name || busy}
                  onClick={() => void confirmDelete()}
                >
                  <i className="bi bi-trash" aria-hidden="true" />
                  {ca.remove}
                </button>
              </div>
            </div>
          </details>
        )}
      </div>
    </Shell>
  );
}
